import { useCallback, useState } from "react";
import { deleteScheduleBlock } from "../../../services/api/schedule";
import { getDateInLocalTZFromDateStr } from "../../../utils/datetime";
import { getAspectBlockId } from "../schedule/model/scheduleBlockSelectors";
import { sameScheduleBlockId } from "../schedule/model/scheduleIds";

/**
 * Map a Dispatch WorkScopeAspect row to the block shape used by delete-scope confirmation.
 *
 * @param {Object|null|undefined} scope Dispatch WorkScopeAspect row.
 * @returns {Object|null} Schedule-like block for {@link requestDeleteScopeBlock}, or null.
 */
export function getDeleteScopeBlockFromDispatchScope(scope) {
  const scheduleBlock = scope?.schedule_block;
  if (scheduleBlock?.id == null) {
    return null;
  }
  return {
    id: scheduleBlock.id,
    scope: { name: scope.scope?.title ?? scope.scope?.name },
    job: scope.job,
    startDate: scheduleBlock.start_date
      ? getDateInLocalTZFromDateStr(scheduleBlock.start_date)
      : null,
    endDate: scheduleBlock.end_date
      ? getDateInLocalTZFromDateStr(scheduleBlock.end_date)
      : null,
  };
}

/**
 * Manage delete-scope confirmation modal and server/local delete flows.
 *
 * Shared by Schedule and Dispatch so both screens run the same block-delete path.
 *
 * @param {Object} params
 * @param {Function} [params.setBlocks] Schedule local block list setter.
 * @param {Function} [params.setWorkScopeAspects] Schedule local aspect list setter.
 * @param {Function} params.runPlanningOperation PlanningContext mutation wrapper.
 * @param {Function} [params.onDeleted] Extra cleanup after a successful delete (e.g. reload Dispatch).
 * @returns {{
 *   deleteScopeBlockModal: Object|null,
 *   deleteScopeBlockBusy: boolean,
 *   requestDeleteScopeBlock: (block: Object) => void,
 *   closeDeleteScopeBlockModal: () => void,
 *   confirmDeleteScopeBlock: () => Promise<void>,
 * }}
 */
export function useDeleteScopeBlock({
  setBlocks,
  setWorkScopeAspects,
  runPlanningOperation,
  onDeleted,
}) {
  const [deleteScopeBlockModal, setDeleteScopeBlockModal] = useState(null);
  const [deleteScopeBlockBusy, setDeleteScopeBlockBusy] = useState(false);

  /**
   * Drop a deleted block from optional Schedule local state, then run extra cleanup.
   *
   * @param {string|number} blockId Deleted block id.
   * @param {Object} block Deleted block object.
   * @returns {void}
   */
  const applyLocalDelete = useCallback(
    (blockId, block) => {
      if (setBlocks) {
        setBlocks((prev) => prev.filter((b) => !sameScheduleBlockId(b.id, blockId)));
      }
      if (setWorkScopeAspects) {
        setWorkScopeAspects((prev) =>
          prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId))
        );
      }
      onDeleted?.(block);
    },
    [onDeleted, setBlocks, setWorkScopeAspects]
  );

  /**
   * Open delete-scope confirmation modal for the selected block.
   * @param {Object} block Timeline block the user asked to delete.
   * @returns {void}
   */
  const requestDeleteScopeBlock = useCallback((block) => {
    setDeleteScopeBlockModal(block ?? null);
  }, []);

  /**
   * Close delete confirmation modal unless a delete request is currently in flight.
   * @param {void}
   * @returns {void}
   */
  const closeDeleteScopeBlockModal = useCallback(() => {
    if (!deleteScopeBlockBusy) {
      setDeleteScopeBlockModal(null);
    }
  }, [deleteScopeBlockBusy]);

  /**
   * Delete selected scope block (or remove optimistic local block) and reconcile linked aspects.
   * @param {void}
   * @returns {Promise<void>}
   */
  const confirmDeleteScopeBlock = useCallback(
    async () => {
      const block = deleteScopeBlockModal;
      if (!block) {
        return;
      }
      const blockId = block.id;
      const idStr = String(blockId);

      if (idStr.startsWith("block-local-")) {
        applyLocalDelete(blockId, block);
        setDeleteScopeBlockModal(null);
        return;
      }

      if (!/^\d+$/.test(idStr)) {
        setDeleteScopeBlockModal(null);
        return;
      }

      setDeleteScopeBlockBusy(true);
      try {
        await runPlanningOperation({
          module: "schedule",
          operation: "deleteScheduleBlock",
          resources: ["schedule.blocks", "schedule.aspects"],
          apiCall: () => deleteScheduleBlock(blockId),
        });
        applyLocalDelete(blockId, block);
        setDeleteScopeBlockModal(null);
      } catch (err) {
        console.error("Schedule block delete failed", err);
      } finally {
        setDeleteScopeBlockBusy(false);
      }
    },
    [applyLocalDelete, deleteScopeBlockModal, runPlanningOperation]
  );

  return {
    deleteScopeBlockModal,
    deleteScopeBlockBusy,
    requestDeleteScopeBlock,
    closeDeleteScopeBlockModal,
    confirmDeleteScopeBlock,
  };
}

export { useDeleteScopeBlock as useScheduleDeleteScopeBlock };
